import React, { useEffect, useState, useCallback } from 'react';
import Modal from './ModalConversion';
import { theme } from '../theme';
import { Loading } from './Loading';
import { endpointGetInactiveTitlesV2, endpointNotifyInactiveTitles, endpointFillThemaFromBisac, urlTasks } from '../services/Endpoints';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faDownload,
    faExclamationCircle,
    faExternalLinkAlt,
    faCheckCircle,
    faTimesCircle,
    faQuestionCircle,
    faFilter,
    faBell,
    faSquareCheck,
    faSquare,
    faTag,
} from '@fortawesome/free-solid-svg-icons';

const PER_PAGE = 50; // Registros por página

// ── Helpers ──────────────────────────────────────────────────────────────────

const csvEscape = (val) => {
    if (val == null || val === '') return '';
    const str = String(val);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
};

const statusBadgeClass = (s) => {
    if (s === 'on') return 'badge-status status-on';
    if (s === 'off') return 'badge-status status-off';
    if (s === 'disabled') return 'badge-status status-disabled';
    return 'badge-status status-unselected';
};

// Unique key para cada título (account + ink_id)
const titleKey = (item) => `${item.account}-${item.ink_id}`;

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * InactiveTitlesModal
 * Props:
 *   isOpen        boolean
 *   onClose       fn
 *   channel       string   — alias del canal
 *   channelLabel  string   — nombre legible
 *   account       string   — cuenta seleccionada (vacío = todas)
 *   status        string   — 'off' | 'disabled'  (default 'off')
 */
function InactiveTitlesModal({ isOpen, onClose, channel, channelLabel, account, status = 'off' }) {
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [notifying, setNotifying] = useState(false);
    const [fillingThema, setFillingThema] = useState(false);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [notifyResult, setNotifyResult] = useState(null);
    const [themaResult, setThemaResult] = useState(null);

    // Paginación
    const [page, setPage] = useState(1);
    const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

    // Selección (se mantiene entre páginas usando titleKey único)
    const [selected, setSelected] = useState(new Set());

    // Filtro activo → va al backend (requiere "Buscar" para aplicarse)
    const [filterActive, setFilterActive] = useState('true');
    const [appliedFilterActive, setAppliedFilterActive] = useState('true');

    // Filtro campo faltante → backend
    const [filterMissingField, setFilterMissingField] = useState('');
    const [appliedFilterMissingField, setAppliedFilterMissingField] = useState('');

    // Filtro válido → backend (requiere "Buscar" para aplicarse)
    const [filterValid, setFilterValid] = useState('');
    const [appliedFilterValid, setAppliedFilterValid] = useState('');

    // ¿Hay filtros de backend sin aplicar?
    const filtersDirty = 
        filterActive !== appliedFilterActive || 
        filterMissingField !== appliedFilterMissingField ||
        filterValid !== appliedFilterValid;

    // displayedItems: los items de la página actual (ya vienen del backend)
    const displayedItems = items;

    // ── Fetch con paginación real (page, per_page=50) ──────────────────────────
    const fetchItems = useCallback(async (activeFilter = 'true', missingFieldFilter = '', validFilter = '', targetPage = 1) => {
        if (!channel) return;
        try {
            setLoading(true);
            const accountParam = account ? `&account=${account}` : '';
            const activeParam = activeFilter !== '' ? `&active=${activeFilter}` : '';
            const missingFieldParam = missingFieldFilter !== '' ? `&missing_field=${missingFieldFilter}` : '';
            const validParam = validFilter !== '' ? `&valid=${validFilter}` : '';
            const url = `${urlTasks}${endpointGetInactiveTitlesV2}?channel=${channel}&status=${status}&page=${targetPage}&per_page=${PER_PAGE}${accountParam}${activeParam}${missingFieldParam}${validParam}`;
            const response = await fetch(url, { method: 'GET', headers: { 'Content-Type': 'application/json' } });
            const res = await response.json();
            if (res && res.status === 'success') {
                setItems(res.data.titles || []);
                setTotal(res.data.total || 0);
                setPage(targetPage);
            } else {
                setItems([]);
                setTotal(0);
                setPage(1);
            }
        } catch (err) {
            console.error('Error fetching inactive titles:', err);
            setItems([]);
            setTotal(0);
            setPage(1);
        } finally {
            setLoading(false);
        }
    }, [channel, account, status]);

    // Reset on open / close
    useEffect(() => {
        if (isOpen && channel) {
            setFilterActive('true');
            setFilterValid('');
            setFilterMissingField('');
            setAppliedFilterActive('true');
            setAppliedFilterMissingField('');
            setAppliedFilterValid('');
            setSelected(new Set());
            setNotifyResult(null);
            setThemaResult(null);
            setPage(1);
            fetchItems('true', '', '', 1);
        }
        if (!isOpen) {
            setItems([]);
            setTotal(0);
            setPage(1);
            setFilterActive('true');
            setFilterValid('');
            setFilterMissingField('');
            setAppliedFilterActive('true');
            setAppliedFilterMissingField('');
            setAppliedFilterValid('');
            setSelected(new Set());
            setNotifyResult(null);
            setThemaResult(null);
        }
    }, [isOpen, channel, account, status, fetchItems]);

    // Aplicar filtros al backend y resetear a página 1
    const handleSearch = () => {
        setSelected(new Set());
        setAppliedFilterActive(filterActive);
        setAppliedFilterMissingField(filterMissingField);
        setAppliedFilterValid(filterValid);
        fetchItems(filterActive, filterMissingField, filterValid, 1);
    };

    // Cambio de página (mantiene filtros aplicados)
    const handlePageChange = (newPage) => {
        if (newPage < 1 || newPage > totalPages || loading) return;
        fetchItems(appliedFilterActive, appliedFilterMissingField, appliedFilterValid, newPage);
    };

    // ── Selección ─────────────────────────────────────────────────────────────
    // Solo se pueden seleccionar títulos activos con datos faltantes
    // valid=null (posiblemente inválido) también es seleccionable
    const isSelectable = (item) => item.active === true && (item.missing_fields || []).length > 0;

    const selectableItems = displayedItems.filter(isSelectable);

    const toggleSelect = (item) => {
        if (!isSelectable(item)) return;
        const key = titleKey(item);
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectableItems.length === 0) return;
        const allSelectableSelected = selectableItems.every(item => selected.has(titleKey(item)));
        setSelected(prev => {
            const next = new Set(prev);
            if (allSelectableSelected) {
                selectableItems.forEach(item => next.delete(titleKey(item)));
            } else {
                selectableItems.forEach(item => next.add(titleKey(item)));
            }
            return next;
        });
    };

    const selectedItems = items.filter(item => selected.has(titleKey(item)));
    const allSelectableSelected = selectableItems.length > 0 && selectableItems.every(item => selected.has(titleKey(item)));
    const someSelected = selected.size > 0;
    // ── Notify ────────────────────────────────────────────────────────────────
    const handleNotify = async () => {
        if (!someSelected) return;
        try {
            setNotifying(true);
            setNotifyResult(null);
            const url = `${urlTasks}${endpointNotifyInactiveTitles}`;
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    channel_label: channelLabel || channel,
                    titles: selectedItems,
                }),
            });
            const res = await response.json();
            if (res && res.status === 'success') {
                setNotifyResult({ ok: true, sent: res.data.sent, errors: res.data.errors || [] });
            } else {
                setNotifyResult({ ok: false, message: res.message || 'Error al enviar notificaciones' });
            }
        } catch (err) {
            setNotifyResult({ ok: false, message: err.message });
        } finally {
            setNotifying(false);
        }
    };

    // ── Fill Thema from BISAC ─────────────────────────────────────────────────
    const handleFillThema = async () => {
        if (!someSelected) return;
        try {
            setFillingThema(true);
            setThemaResult(null);
            // Construir la lista en formato account-ink_id separada por comas
            const titlesList = selectedItems.map(item => `${item.account}-${item.ink_id}`).join(',');
            const url = `${urlTasks}${endpointFillThemaFromBisac}?titles=${encodeURIComponent(titlesList)}`;
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
            const res = await response.json();
            if (res && res.status === 'success') {
                setThemaResult({
                    ok: true,
                    message: res.data.message || 'Proceso iniciado en background.',
                    total: res.data.total_to_process,
                });
            } else {
                setThemaResult({ ok: false, message: res.message || 'Error al iniciar el proceso.' });
            }
        } catch (err) {
            setThemaResult({ ok: false, message: err.message });
        } finally {
            setFillingThema(false);
        }
    };

    // ── CSV Export ────────────────────────────────────────────────────────────
    const handleExportCsv = async () => {
        if (!channel || total === 0) return;
        try {
            setExporting(true);
            const accountParam = account ? `&account=${account}` : '';
            const activeParam = appliedFilterActive !== '' ? `&active=${appliedFilterActive}` : '';
            const missingFieldParam = appliedFilterMissingField !== '' ? `&missing_field=${appliedFilterMissingField}` : '';
            const url = `${urlTasks}${endpointGetInactiveTitlesV2}?channel=${channel}&status=${status}&page=1&per_page=${total}${accountParam}${activeParam}${missingFieldParam}`;
            const response = await fetch(url, { method: 'GET', headers: { 'Content-Type': 'application/json' } });
            const res = await response.json();
            if (!res || res.status !== 'success') throw new Error('Error al obtener datos');

            const allItems = res.data.titles || [];
            const headers = ['Título', 'Cuenta', 'Ink ID', 'ISBN', '656_id', '409_id', 'Activo', 'Válido', 'Status Canal', 'Link Canal', 'Campos faltantes'];
            const rows = allItems.map((item) => {
                const ids = item.internal_ids || {};
                return [
                    csvEscape(item.title), csvEscape(item.account), csvEscape(item.ink_id),
                    csvEscape(item.isbn), csvEscape(ids['656_id']), csvEscape(ids['409_id']),
                    csvEscape(item.active ? 'Sí' : 'No'), csvEscape(item.valid ? 'Sí' : 'No'),
                    csvEscape(item.channel_status), csvEscape(item.channel_link),
                    csvEscape((item.missing_fields || []).join(' | ')),
                ].join(',');
            });

            const csvContent = [headers.join(','), ...rows].join('\r\n');
            const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
            const blobUrl = URL.createObjectURL(blob);
            const dateStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
            const acctSuffix = account ? `_${account}` : '';
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `inactivos_${channel}${acctSuffix}_${dateStr}.csv`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error('Error exportando CSV:', err);
            alert('Error al exportar CSV: ' + err.message);
        } finally {
            setExporting(false);
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Modal isOpen={isOpen} onClose={onClose} fullscreen>
            <div style={{ minHeight: 200 }}>
                {/* Header */}
                <div className="d-flex align-items-center gap-2 mb-1" style={{ flexWrap: 'wrap' }}>
                    <span style={{ color: '#f59e0b', fontSize: '1.3rem' }}>
                        <FontAwesomeIcon icon={faExclamationCircle} />
                    </span>
                    <div>
                        <h4 className="mb-0 text-white" style={{ fontWeight: 700 }}>
                            Títulos inactivos en tienda
                        </h4>
                        <p className="mb-0" style={{ color: 'rgba(var(--ink-overlay-rgb),0.55)', fontSize: '0.85rem' }}>
                            Canal: <strong style={{ color: 'var(--ink-text)' }}>{channelLabel || channel}</strong>
                            {account && (<> &nbsp;·&nbsp; Cuenta: <strong style={{ color: 'var(--ink-text)' }}>{account}</strong></>)}
                            &nbsp;·&nbsp; Status: <strong style={{ color: 'var(--ink-text)' }}>{status}</strong>
                        </p>
                    </div>
                </div>

                {/* Badge + filtros + acciones */}
                <div className="d-flex align-items-center gap-2 mb-3" style={{ flexWrap: 'wrap' }}>
                    {/* Contador — siempre visible para no perder contexto durante la carga */}
                    {!loading && (
                        <span className="badge-status status-off" style={{ fontSize: '0.8rem', padding: '5px 12px' }}>
                            {total.toLocaleString()} título{total !== 1 ? 's' : ''} inactivo{total !== 1 ? 's' : ''}
                        </span>
                    )}

                    {/* Filtro Activo → va al backend, requiere Buscar */}
                    <div className="d-flex align-items-center gap-1">
                        <FontAwesomeIcon icon={faFilter} style={{ color: 'rgba(var(--ink-overlay-rgb),0.4)', fontSize: '0.75rem' }} />
                        <select
                            className="form-select form-select-sm"
                            style={{
                                width: 130,
                                background: theme.surfaceRaised,
                                color: filtersDirty ? '#fbbf24' : 'var(--ink-text)',
                                border: filtersDirty
                                    ? '1px solid rgba(245,158,11,0.6)'
                                    : '1px solid rgba(var(--ink-overlay-rgb),0.15)',
                                fontSize: '0.8rem',
                            }}
                            value={filterActive}
                            onChange={e => setFilterActive(e.target.value)}
                            title="Filtrar por campo activo — afecta total y paginado (requiere Buscar)"
                        >
                            <option value="">Activo: todos</option>
                            <option value="true">Activo: Sí</option>
                            <option value="false">Activo: No</option>
                        </select>
                    </div>

                    {/* Filtro Válido → client-side instantáneo, solo página actual */}
                    <div className="d-flex align-items-center gap-1">
                        <FontAwesomeIcon icon={faFilter} style={{ color: 'rgba(var(--ink-overlay-rgb),0.4)', fontSize: '0.75rem' }} />
                        <select
                            className="form-select form-select-sm"
                            style={{
                                width: 145,
                                background: theme.surfaceRaised,
                                color: filtersDirty && filterValid !== appliedFilterValid ? '#fbbf24' : 'var(--ink-text)',
                                border: filtersDirty && filterValid !== appliedFilterValid
                                    ? '1px solid rgba(245,158,11,0.6)'
                                    : '1px solid rgba(var(--ink-overlay-rgb),0.15)',
                                fontSize: '0.8rem',
                            }}
                            value={filterValid}
                            onChange={e => setFilterValid(e.target.value)}
                            title="Filtrar por validez (requiere Buscar)"
                        >
                            <option value="">Válido: todos</option>
                            <option value="true">Válido: Sí</option>
                            <option value="false">Válido: No / ?</option>
                        </select>
                    </div>

                    {/* Filtro Campo Faltante → backend */}
                    <div className="d-flex align-items-center gap-1">
                        <FontAwesomeIcon icon={faFilter} style={{ color: 'rgba(var(--ink-overlay-rgb),0.4)', fontSize: '0.75rem' }} />
                        <select
                            className="form-select form-select-sm"
                            style={{
                                width: 170,
                                background: theme.surfaceRaised,
                                color: filtersDirty && filterMissingField !== appliedFilterMissingField ? '#fbbf24' : 'var(--ink-text)',
                                border: filtersDirty && filterMissingField !== appliedFilterMissingField
                                    ? '1px solid rgba(245,158,11,0.6)'
                                    : '1px solid rgba(var(--ink-overlay-rgb),0.15)',
                                fontSize: '0.8rem',
                            }}
                            value={filterMissingField}
                            onChange={e => setFilterMissingField(e.target.value)}
                            title="Filtrar por campo faltante (requiere Buscar)"
                        >
                            <option value="">Faltante: cualquiera</option>
                            <option value="assets.ebook">Faltante: Ebook</option>
                            <option value="assets.cover">Faltante: Portada</option>
                            <option value="description">Faltante: Descripción</option>
                            <option value="language">Faltante: Idioma</option>
                            <option value="imprint">Faltante: Sello</option>
                            <option value="publication_date">Faltante: Fecha pub.</option>
                            <option value="collaborators">Faltante: Colaboradores</option>
                            <option value="categories_bisac">Faltante: BISAC</option>
                            <option value="categories_thema">Faltante: Thema</option>
                            <option value="age_interest">Faltante: Edad interés</option>
                            <option value="drm">Faltante: DRM</option>
                            <option value="prices">Faltante: Precios</option>
                            <option value="channels">Faltante: Canales</option>
                            <option value="my_store.prices">Faltante: Precios (Mi Tienda)</option>
                            <option value="my_store.collections">Faltante: Col. (Mi Tienda)</option>
                            <option value="my_store.categories">Faltante: Cat. (Mi Tienda)</option>
                        </select>
                    </div>

                    {/* Botón Buscar → aplica filterActive al backend */}
                    <button
                        className="btn btn-sm d-flex align-items-center gap-2"
                        style={{
                            background: filtersDirty ? '#f59e0b' : 'rgba(var(--ink-overlay-rgb),0.1)',
                            color: filtersDirty ? '#1f2731' : 'rgba(var(--ink-overlay-rgb),0.8)',
                            border: filtersDirty ? '1px solid #f59e0b' : '1px solid rgba(var(--ink-overlay-rgb),0.15)',
                            fontWeight: filtersDirty ? 700 : 400,
                            transition: 'all 0.2s',
                        }}
                        onClick={handleSearch}
                        disabled={loading}
                        title={filtersDirty ? 'Hay filtros sin aplicar — haz clic para buscar' : 'Buscar con los filtros actuales'}
                    >
                        <FontAwesomeIcon icon={faFilter} />
                        {loading ? 'Buscando…' : filtersDirty ? 'Buscar ●' : 'Buscar'}
                    </button>

                    {/* Botón Notificar — siempre visible, disabled sin selección */}
                    {!loading && (
                        <button
                            className="btn btn-warning btn-sm d-flex align-items-center gap-2"
                            onClick={handleNotify}
                            disabled={notifying || !someSelected}
                            title={someSelected
                                ? `Notificar a los usuarios de ${selected.size} título(s) seleccionado(s)`
                                : 'Selecciona títulos activos con datos faltantes para notificar'}
                        >
                            <FontAwesomeIcon icon={faBell} spin={notifying} />
                            {notifying ? 'Enviando…' : `Notificar${someSelected ? ` (${selected.size})` : ''}`}
                        </button>
                    )}

                    {/* Botón Fill Thema — asigna categorías Thema desde BISAC */}
                    {!loading && (
                        <button
                            className="btn btn-sm d-flex align-items-center gap-2"
                            style={{
                                background: someSelected ? `rgba(\${theme.primaryRgb}, 0.15)` : 'rgba(var(--ink-overlay-rgb),0.06)',
                                color: someSelected ? theme.primary : 'rgba(var(--ink-overlay-rgb),0.3)',
                                border: someSelected
                                    ? `1px solid rgba(${theme.primaryRgb}, 0.5)`
                                    : '1px solid rgba(var(--ink-overlay-rgb),0.1)',
                                transition: 'all 0.2s',
                                cursor: someSelected ? 'pointer' : 'not-allowed',
                            }}
                            onClick={handleFillThema}
                            disabled={fillingThema || !someSelected}
                            title={someSelected
                                ? `Asignar Thema desde BISAC a ${selected.size} título(s) seleccionado(s)`
                                : 'Selecciona títulos para asignar categorías Thema'}
                        >
                            <FontAwesomeIcon icon={faTag} spin={fillingThema} />
                            {fillingThema ? 'Procesando…' : `Fill Thema${someSelected ? ` (${selected.size})` : ''}`}
                        </button>
                    )}

                    {/* Exportar CSV */}
                    {!loading && total > 0 && (
                        <button
                            className="btn btn-outline-success btn-sm d-flex align-items-center gap-2"
                            onClick={handleExportCsv}
                            disabled={exporting}
                            title="Exportar todos los registros a CSV"
                        >
                            <FontAwesomeIcon icon={faDownload} spin={exporting} />
                            {exporting ? 'Exportando…' : 'Exportar CSV'}
                        </button>
                    )}
                </div>

                {/* Resultado de notificación */}
                {notifyResult && (
                    <div
                        className={`alert ${notifyResult.ok ? 'alert-success' : 'alert-danger'} py-2 px-3 mb-3`}
                        style={{ fontSize: '0.85rem' }}
                    >
                        {notifyResult.ok
                            ? `✅ Enviados ${notifyResult.sent} correo(s) exitosamente.${notifyResult.errors.length > 0 ? ` ${notifyResult.errors.length} error(es).` : ''}`
                            : `❌ ${notifyResult.message}`
                        }
                    </div>
                )}

                {/* Resultado de Fill Thema */}
                {themaResult && (
                    <div
                        className={`alert ${themaResult.ok ? 'alert-info' : 'alert-danger'} py-2 px-3 mb-3`}
                        style={{ fontSize: '0.85rem', borderColor: themaResult.ok ? `rgba(\${theme.primaryRgb}, 0.4)` : undefined, background: themaResult.ok ? `rgba(\${theme.primaryRgb}, 0.1)` : undefined, color: themaResult.ok ? theme.primaryHover : undefined }}
                    >
                        {themaResult.ok
                            ? `🏷️ ${themaResult.message}${themaResult.total != null ? ` (${themaResult.total} título(s))` : ''}`
                            : `❌ ${themaResult.message}`
                        }
                    </div>
                )}

                {loading && <Loading />}

                {!loading && items.length === 0 && (
                    <div className="text-center py-5" style={{ color: 'rgba(var(--ink-overlay-rgb),0.45)' }}>
                        <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>✅</div>
                        <p>No se encontraron títulos inactivos con los filtros seleccionados.</p>
                    </div>
                )}

                {!loading && items.length > 0 && displayedItems.length === 0 && (
                    <div className="text-center py-4" style={{ color: 'rgba(var(--ink-overlay-rgb),0.45)' }}>
                        <p style={{ fontSize: '0.9rem' }}>No se encontraron títulos con los filtros seleccionados.</p>
                    </div>
                )}

                {!loading && items.length > 0 && displayedItems.length > 0 && (
                    <>
                        <div className="modern-table-container">
                            <table className="modern-table table-hover">
                                <thead>
                                    <tr>
                                        {/* Checkbox select all — sólo sobre elegibles */}
                                        <th style={{ width: 36, textAlign: 'center' }}>
                                            <span
                                                style={{
                                                    cursor: selectableItems.length > 0 ? 'pointer' : 'default',
                                                    fontSize: '1rem',
                                                    color: allSelectableSelected && selectableItems.length > 0
                                                        ? '#22c55e'
                                                        : 'rgba(var(--ink-overlay-rgb),0.35)'
                                                }}
                                                onClick={toggleSelectAll}
                                                title={selectableItems.length > 0
                                                    ? (allSelectableSelected ? 'Deseleccionar todos' : 'Seleccionar todos los elegibles')
                                                    : 'Sin títulos elegibles en esta página'}
                                            >
                                                <FontAwesomeIcon icon={allSelectableSelected && selectableItems.length > 0 ? faSquareCheck : faSquare} />
                                            </span>
                                        </th>
                                        <th style={{ width: 40 }}>#</th>
                                        <th style={{ minWidth: 200 }}>Título</th>
                                        <th>Cuenta</th>
                                        <th>Ink ID</th>
                                        <th style={{ minWidth: 130 }}>Identificador</th>
                                        <th className="text-center" style={{ width: 70 }}>Activo</th>
                                        <th className="text-center" style={{ width: 70 }}>Válido</th>
                                        <th className="text-center" style={{ width: 100 }}>Status</th>
                                        <th style={{ width: 120 }}>Link</th>
                                        <th style={{ minWidth: 180 }}>Campos faltantes</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayedItems.map((item, idx) => {
                                        const rowNum = (page - 1) * PER_PAGE + idx + 1;
                                        const internalIds = item.internal_ids || {};
                                        const missingFields = item.missing_fields || [];
                                        const key = titleKey(item);
                                        const isChecked = selected.has(key);
                                        return (
                                            <tr
                                                key={idx}
                                                style={{
                                                    background: isChecked ? 'rgba(245,158,11,0.08)' : undefined,
                                                    cursor: isSelectable(item) ? 'pointer' : 'default',
                                                    opacity: isSelectable(item) ? 1 : 0.65,
                                                }}
                                                onClick={() => isSelectable(item) && toggleSelect(item)}
                                            >
                                                {/* Checkbox */}
                                                <td className="text-center" onClick={e => e.stopPropagation()}>
                                                    <span
                                                        style={{
                                                            cursor: isSelectable(item) ? 'pointer' : 'not-allowed',
                                                            fontSize: '1rem',
                                                            color: isChecked
                                                                ? '#f59e0b'
                                                                : isSelectable(item)
                                                                    ? 'rgba(var(--ink-overlay-rgb),0.4)'
                                                                    : 'rgba(var(--ink-overlay-rgb),0.15)',
                                                        }}
                                                        onClick={() => isSelectable(item) && toggleSelect(item)}
                                                        title={isSelectable(item)
                                                            ? (isChecked ? 'Deseleccionar' : 'Seleccionar')
                                                            : 'Solo se pueden seleccionar títulos activos con datos faltantes'}
                                                    >
                                                        <FontAwesomeIcon icon={isChecked ? faSquareCheck : faSquare} />
                                                    </span>
                                                </td>
                                                {/* # */}
                                                <td style={{ color: 'rgba(var(--ink-overlay-rgb),0.35)', fontSize: '0.8rem' }}>{rowNum}</td>
                                                {/* Título */}
                                                <td
                                                    style={{ fontSize: '0.85rem', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                    title={item.title || undefined}
                                                >
                                                    {item.title || <span style={{ opacity: 0.35 }}>—</span>}
                                                </td>
                                                {/* Cuenta */}
                                                <td style={{ fontWeight: 600, color: theme.primary, fontSize: '0.85rem' }}>{item.account || '—'}</td>
                                                {/* Ink ID */}
                                                <td style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>
                                                    {item.ink_id != null ? item.ink_id : <span style={{ opacity: 0.35 }}>—</span>}
                                                </td>
                                                {/* Identificador: ISBN si existe, si no 656_id / 409_id */}
                                                <td style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>
                                                    {item.isbn
                                                        ? item.isbn
                                                        : (
                                                            <>
                                                                {internalIds['656_id'] && (
                                                                    <div title="656_id">
                                                                        <span style={{ color: 'rgba(var(--ink-overlay-rgb),0.4)', fontSize: '0.7rem', marginRight: 3 }}>656</span>
                                                                        {internalIds['656_id']}
                                                                    </div>
                                                                )}
                                                                {internalIds['409_id'] && (
                                                                    <div title="409_id">
                                                                        <span style={{ color: 'rgba(var(--ink-overlay-rgb),0.4)', fontSize: '0.7rem', marginRight: 3 }}>409</span>
                                                                        {internalIds['409_id']}
                                                                    </div>
                                                                )}
                                                                {!internalIds['656_id'] && !internalIds['409_id'] && (
                                                                    <span style={{ opacity: 0.35 }}>—</span>
                                                                )}
                                                            </>
                                                        )
                                                    }
                                                </td>
                                                {/* Activo */}
                                                <td className="text-center">
                                                    <FontAwesomeIcon
                                                        icon={item.active ? faCheckCircle : faTimesCircle}
                                                        style={{ color: item.active ? '#22c55e' : '#ef4444', fontSize: '1rem' }}
                                                        title={item.active ? 'Activo' : 'Inactivo'}
                                                    />
                                                </td>
                                                {/* Válido — tres estados: true / null (posible) / false */}
                                                <td className="text-center">
                                                    {item.valid === true && (
                                                        <FontAwesomeIcon
                                                            icon={faCheckCircle}
                                                            style={{ color: '#22c55e', fontSize: '1rem' }}
                                                            title="Válido"
                                                        />
                                                    )}
                                                    {item.valid === null && (
                                                        <FontAwesomeIcon
                                                            icon={faQuestionCircle}
                                                            style={{ color: theme.primary, fontSize: '1rem' }}
                                                            title="Posiblemente inválido (epub_metadata no verificada)"
                                                        />
                                                    )}
                                                    {item.valid === false && (
                                                        <FontAwesomeIcon
                                                            icon={faTimesCircle}
                                                            style={{ color: '#f59e0b', fontSize: '1rem' }}
                                                            title="Inválido"
                                                        />
                                                    )}
                                                </td>
                                                {/* Status canal */}
                                                <td className="text-center">
                                                    <span className={statusBadgeClass(item.channel_status)} style={{ fontSize: '0.75rem', padding: '3px 8px' }}>
                                                        {item.channel_status || '—'}
                                                    </span>
                                                </td>
                                                {/* Link */}
                                                <td style={{ fontSize: '0.8rem' }}>
                                                    {item.channel_link && item.channel_link !== '-' && item.channel_link !== '' ? (
                                                        <a
                                                            href={item.channel_link}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{ color: theme.primary, textDecoration: 'underline' }}
                                                            onClick={e => e.stopPropagation()}
                                                        >
                                                            <FontAwesomeIcon icon={faExternalLinkAlt} size="sm" /> Ver
                                                        </a>
                                                    ) : (
                                                        <span style={{ opacity: 0.35 }}>—</span>
                                                    )}
                                                </td>
                                                {/* Campos faltantes */}
                                                <td style={{ fontSize: '0.78rem' }}>
                                                    {missingFields.length > 0 ? (
                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                                            {missingFields.map((f, i) => {
                                                                const isWarning = f.endsWith('?');
                                                                return (
                                                                    <span key={i} style={{
                                                                        background: isWarning
                                                                            ? `rgba(\${theme.primaryRgb}, 0.12)`
                                                                            : 'rgba(245,158,11,0.15)',
                                                                        color: isWarning ? theme.primary : '#fbbf24',
                                                                        border: `1px solid ${isWarning ? `rgba(\${theme.primaryRgb}, 0.35)` : 'rgba(245,158,11,0.3)'}`,
                                                                        borderRadius: 4,
                                                                        padding: '2px 6px',
                                                                        fontSize: '0.72rem',
                                                                        whiteSpace: 'nowrap',
                                                                    }}
                                                                        title={isWarning ? 'Advertencia: no verificado' : 'Campo faltante'}
                                                                    >
                                                                        {isWarning ? '⚠ ' : ''}{f}
                                                                    </span>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : (
                                                        <span style={{ opacity: 0.35 }}>—</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Paginación + contador */}
                        <div className="d-flex justify-content-between align-items-center gap-2 mt-3" style={{ flexWrap: 'wrap' }}>
                            {/* Info de registros */}
                            <span style={{ color: 'rgba(var(--ink-overlay-rgb),0.55)', fontSize: '0.82rem' }}>
                                Mostrando{' '}
                                <strong style={{ color: 'var(--ink-text)' }}>
                                    {total === 0 ? 0 : (page - 1) * PER_PAGE + 1}
                                </strong>
                                {' – '}
                                <strong style={{ color: 'var(--ink-text)' }}>
                                    {Math.min(page * PER_PAGE, total)}
                                </strong>
                                {' de '}
                                <strong style={{ color: 'var(--ink-text)' }}>{total.toLocaleString()}</strong>
                                {' registro'}{total !== 1 ? 's' : ''}
                                {someSelected && (
                                    <>
                                        {' '}&nbsp;·&nbsp;{' '}
                                        <strong style={{ color: '#f59e0b' }}>
                                            {selected.size} seleccionado{selected.size !== 1 ? 's' : ''}
                                        </strong>
                                    </>
                                )}
                            </span>

                            {/* Controles de página */}
                            {totalPages > 1 && (
                                <div className="d-flex align-items-center gap-2">
                                    <button
                                        className="btn btn-sm"
                                        style={{
                                            background: page <= 1 ? 'rgba(var(--ink-overlay-rgb),0.05)' : 'rgba(var(--ink-overlay-rgb),0.12)',
                                            color: page <= 1 ? 'rgba(var(--ink-overlay-rgb),0.25)' : 'var(--ink-text)',
                                            border: '1px solid rgba(var(--ink-overlay-rgb),0.15)',
                                            padding: '3px 10px',
                                            fontSize: '0.8rem',
                                            cursor: page <= 1 ? 'not-allowed' : 'pointer',
                                        }}
                                        onClick={() => handlePageChange(page - 1)}
                                        disabled={page <= 1 || loading}
                                    >
                                        ← Anterior
                                    </button>

                                    {/* Selector de página */}
                                    <div className="d-flex align-items-center gap-1" style={{ fontSize: '0.82rem', color: 'rgba(var(--ink-overlay-rgb),0.7)' }}>
                                        <span>Pág.</span>
                                        <select
                                            className="form-select form-select-sm"
                                            style={{
                                                width: 70,
                                                background: theme.surfaceRaised,
                                                color: 'var(--ink-text)',
                                                border: '1px solid rgba(var(--ink-overlay-rgb),0.2)',
                                                fontSize: '0.8rem',
                                                padding: '2px 4px',
                                            }}
                                            value={page}
                                            onChange={e => handlePageChange(Number(e.target.value))}
                                            disabled={loading}
                                        >
                                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                                                <option key={p} value={p}>{p}</option>
                                            ))}
                                        </select>
                                        <span style={{ color: 'rgba(var(--ink-overlay-rgb),0.4)' }}>de {totalPages}</span>
                                    </div>

                                    <button
                                        className="btn btn-sm"
                                        style={{
                                            background: page >= totalPages ? 'rgba(var(--ink-overlay-rgb),0.05)' : 'rgba(var(--ink-overlay-rgb),0.12)',
                                            color: page >= totalPages ? 'rgba(var(--ink-overlay-rgb),0.25)' : 'var(--ink-text)',
                                            border: '1px solid rgba(var(--ink-overlay-rgb),0.15)',
                                            padding: '3px 10px',
                                            fontSize: '0.8rem',
                                            cursor: page >= totalPages ? 'not-allowed' : 'pointer',
                                        }}
                                        onClick={() => handlePageChange(page + 1)}
                                        disabled={page >= totalPages || loading}
                                    >
                                        Siguiente →
                                    </button>
                                </div>
                            )}
                        </div>
                    </>
                )}

                {/* Footer */}
                <div className="d-flex justify-content-end mt-4 pt-3 border-top border-secondary">
                    <button className="btn btn-secondary px-4" onClick={onClose}>
                        Cerrar
                    </button>
                </div>
            </div>
        </Modal>
    );
}

export default InactiveTitlesModal;
